import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Commercial Surveys live in the tech server (it holds the forms, the answers
// and the photos); the dashboard forwards and tech applies the gate.
const KEY = 'commercialSurveys';

export function useSurveys(filters) {
  return useQuery({
    queryKey: [KEY, 'list', filters],
    queryFn: async () => (await api.get('/api/commercial-surveys', { params: filters })).data,
    staleTime: 30000,
  });
}

export function useSurvey(id) {
  return useQuery({
    queryKey: [KEY, 'one', id],
    queryFn: async () => (await api.get(`/api/commercial-surveys/${id}`)).data,
    enabled: Boolean(id),
  });
}

// Everyone a survey can be assigned to.
export function useSurveyStaff() {
  return useQuery({
    queryKey: [KEY, 'staff'],
    queryFn: async () => (await api.get('/api/commercial-surveys/staff')).data.staff,
    staleTime: 300000,
  });
}

export function useSaveSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => (id
      ? (await api.put(`/api/commercial-surveys/${id}`, body)).data
      : (await api.post('/api/commercial-surveys', body)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

// The questions, their options and their branching - the same definition the
// FSM app renders from.
export function useSurveyForm(formKey) {
  return useQuery({
    queryKey: [KEY, 'form', formKey],
    queryFn: async () => (await api.get(`/api/commercial-surveys/forms/${formKey}`)).data,
    enabled: Boolean(formKey),
    staleTime: 600000,
  });
}

// What the surveyor answered, only fetched when somebody opens it. A named
// submission reads back an earlier survey of the same address.
export function useSubmission(surveyId, formKey, submissionId) {
  return useQuery({
    queryKey: [KEY, 'submission', surveyId, formKey, submissionId || 'latest'],
    queryFn: async () => (await api.get(`/api/commercial-surveys/${surveyId}/submission/${formKey}`, {
      params: submissionId ? { submission: submissionId } : {},
    })).data,
    enabled: Boolean(surveyId && formKey),
  });
}

// Every attempt at a form on this survey: a property can be surveyed more than
// once, and the office reads them all.
export function useSubmissions(surveyId, formKey) {
  return useQuery({
    queryKey: [KEY, 'submissions', surveyId, formKey],
    queryFn: async () => (await api.get(`/api/commercial-surveys/${surveyId}/submissions`, {
      params: { form_key: formKey },
    })).data.submissions,
    enabled: Boolean(surveyId && formKey),
  });
}

// The record as a PDF, for sending to the customer or filing.
//
// Fetched through the API rather than linked: the endpoint needs the bearer
// token, so a plain <a href> would 401. The blob is handed to the browser under
// the filename the server chose, which matches the old NEO1 export so the two
// sit together in a folder.
export function useSurveyPdf() {
  return useMutation({
    mutationFn: async ({ surveyId, formKey }) => {
      const res = await api.get(`/api/commercial-surveys/${surveyId}/${formKey}.pdf`, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const named = /filename="?([^"]+)"?/.exec(disposition);
      const filename = named ? named[1] : `survey-${surveyId}-${formKey}.pdf`;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick: Safari cancels the download if the URL goes
      // before it has read it.
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return filename;
    },
  });
}
